import { Checkbox, Popover } from '@faceup/ui-base'
import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

export type ScrollableCheckboxListOption = {
  id: string
  label: ReactNode
  disabled?: boolean
  popoverContent?: ReactNode
}

export type ScrollableCheckboxListProps = {
  options: ScrollableCheckboxListOption[]
  selectedIds: string[]
  onChange: (id: string, checked: boolean) => void
  'aria-label'?: string
  'aria-describedby'?: string
  className?: string
}

export const ScrollableCheckboxList = ({
  options,
  selectedIds,
  onChange,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  className,
}: ScrollableCheckboxListProps) => {
  const selected = new Set(selectedIds)

  return (
    <fieldset
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={twMerge('m-0 flex flex-col rounded-xl border border-colorBorder p-1', className)}
    >
      {/* The cut half row is what tells the reporter the list scrolls, now that the scrollbar is
          the browser's own and stays hidden until it does. 1lh is one row, 0.5rem the gap-2 and
          the py-2. */}
      <div className='flex max-h-[calc(6*(1lh+0.5rem)+0.5lh+0.5rem)] min-h-0 flex-col gap-2 overflow-y-auto px-3 py-2'>
        {options.map(option => {
          const checkbox = (
            <Checkbox
              checked={selected.has(option.id)}
              disabled={option.disabled}
              onChange={e => onChange(option.id, e.target.checked)}
            >
              {option.label}
            </Checkbox>
          )

          if (option.popoverContent) {
            return (
              <Popover key={option.id} placement='bottom' content={option.popoverContent}>
                {checkbox}
              </Popover>
            )
          }

          return <div key={option.id}>{checkbox}</div>
        })}
      </div>
    </fieldset>
  )
}
